import type { AgentPayout } from '@kilat/shared';

import { useI18n } from '../lib/use-i18n.js';

type PayoutStatus = AgentPayout['status'];

/**
 * Coloured status pill for a payout batch (pending / processing / paid /
 * failed). Shared by the agent + recruiter payout-history surfaces. Copy
 * comes from `agent.payouts.status.*` via `useI18n()`.
 */
export function PayoutStatusPill({ status }: { readonly status: PayoutStatus }) {
  const t = useI18n();
  const visual = visualFor(status);
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: visual.bg,
        color: visual.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {t.t(`agent.payouts.status.${status}`)}
    </span>
  );
}

function visualFor(status: PayoutStatus): { bg: string; fg: string } {
  switch (status) {
    case 'paid':
      return { bg: 'rgba(13,122,79,0.12)', fg: 'var(--color-primary)' };
    case 'processing':
      return { bg: 'rgba(59,130,246,0.12)', fg: '#1d4ed8' };
    case 'pending':
      return { bg: 'rgba(245,158,11,0.16)', fg: '#b45309' };
    case 'failed':
      return { bg: 'rgba(239,68,68,0.14)', fg: '#b91c1c' };
  }
}
